import React, { useMemo } from "react"
import { Product_Output, ProductionTask_Input } from "@/api"
import SeparablePanel from "@/components/SeparablePanel"
import { ItemStats } from "./ItemTreeTable"

interface ItemMasterDetailProps {
  products: Product_Output[]
  itemStats: Record<Product_Output["sku_id"], ItemStats>
  productionTasks?: Array<ProductionTask_Input & { box_count?: number | null; pl_count?: number | null; material_kg?: number | null }>
  onSelectionChange?: (products: Product_Output[]) => void
}

const formatMaybe = (v: unknown) => {
  if (v === undefined || v === null) return '-'
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v.toLocaleString('en-US')
  }
  return String(v)
}

const formatFloat = (v: number | null | undefined) => {
  if (v === undefined || v === null) return '-'
  return v.toFixed(1)
}

const formatBoolean = (v: boolean | null | undefined) => {
  if (v === undefined || v === null) return '-'
  return v ? '〇' : '×'
}

type DisplayRow = {
  key: string
  isVariant: boolean
  id: string | null
  name: string
  category: string | null
  machine: string | null
  sheetIdx: number | null
  maxInventory: number | null | undefined
  minLot: number | null | undefined
  fixedBatch: number | null | undefined
  qtyPerBox: number | null | undefined
  qtyPerPL: number | null | undefined
  cycleTimeSec: number | null | undefined
  quantityPerCycle: number | null | undefined
  defectRate: number | null | undefined
  requiresAssembly: boolean | null | undefined
  dualInspection: boolean | null | undefined
  note: string | null | undefined
  totalOrders: number | undefined
  totalMolding: number | undefined
  totalBoxCount: number | undefined
  totalPlCount: number | undefined
  totalMaterialKg: number | undefined
}

const ItemMasterDetail: React.FC<ItemMasterDetailProps> = ({ products, itemStats, productionTasks = [] }) => {
  const taskTotalsBySku = useMemo(() => {
    const totals = new Map<string, { boxCount: number; plCount: number; materialKg: number }>()
    productionTasks.forEach((task) => {
      const current = totals.get(task.sku_id) ?? { boxCount: 0, plCount: 0, materialKg: 0 }
      current.boxCount += task.box_count ?? 0
      current.plCount += task.pl_count ?? 0
      current.materialKg += task.material_kg ?? 0
      totals.set(task.sku_id, current)
    })
    return totals
  }, [productionTasks])

  // echoProduct_Output と machine_variants を親子で表示する
  const rows: DisplayRow[] = products.flatMap((product) => {
    const variants = Object.values(product.machine_variants ?? {})
    const taskTotals = taskTotalsBySku.get(product.sku_id)

    const parentRow: DisplayRow = {
      key: `product-${product.sku_id}`,
      isVariant: false,
      id: product.sku_id,
      name: product.name,
      category: product.category,
      machine: product.machine_sheet ?? null,
      sheetIdx: null,
      maxInventory: product.max_inventory,
      minLot: product.min_lot_size,
      fixedBatch: null,
      qtyPerBox: product.quantity_per_box ?? null,
      qtyPerPL: product.quantity_per_pl ?? null,
      cycleTimeSec: null,
      quantityPerCycle: product.quantity_per_cycle ?? null,
      defectRate: product.defect_rate ?? null,
      requiresAssembly: null,
      dualInspection: null,
      note: null,
      totalOrders: itemStats[product.sku_id]?.総注文数,
      totalMolding: itemStats[product.sku_id]?.総成形数,
      totalBoxCount: taskTotals?.boxCount,
      totalPlCount: taskTotals?.plCount,
      totalMaterialKg: taskTotals?.materialKg,
    }

    if (!variants.length) {
      return [parentRow]
    }

    const variantRows: DisplayRow[] = variants.map((variant, idx) => ({
      key: `variant-${product.sku_id}-${idx}`,
      isVariant: true,
      id: null,
      name: product.name,
      category: product.category,
      machine: variant.machine_id,
      sheetIdx: variant.sheet_idx,
      maxInventory: null,
      minLot: null,
      fixedBatch: null,
      qtyPerBox: null,
      qtyPerPL: null,
      cycleTimeSec: variant.cycle_time_sec,
      quantityPerCycle: null,
      defectRate: null,
      requiresAssembly: variant.requires_assembly,
      dualInspection: variant.is_dual_inspection_compatible,
      note: variant.note,
      totalOrders: undefined,
      totalMolding: undefined,
      totalBoxCount: undefined,
      totalPlCount: undefined,
      totalMaterialKg: undefined,
    }))

    return [parentRow, ...variantRows]
  })

  return (
    <SeparablePanel toggleable header="マスター情報">
      <div className="py-2 flex flex-column gap-3">
        {rows
          .filter((r) => !r.isVariant)
          .map((parent) => {
            const variants = rows.filter((r) => r.isVariant && r.name === parent.name)
            return (
              <div key={parent.key} className="border-1 border-300 border-round p-3 flex flex-column gap-2 bg-white">
                <div className="flex flex-column gap-1">
                  <div className="text-lg text-600">{formatMaybe(parent.id)}</div>
                  <div className="text-lg font-semibold">{formatMaybe(parent.category)}</div>
                  <div className="text-700">成形シート: {formatMaybe(parent.machine)}：{formatMaybe(parent.name)}</div>
                </div>
                <div className="grid text-sm">
                  <div className="col-12 sm:col-6 md:col-4 flex justify-content-between">
                    <span className="text-600">サイクルあたりの成形数</span><span>{formatMaybe(parent.quantityPerCycle)}</span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-4 flex justify-content-between">
                    <span className="text-600">不良</span><span>{formatFloat((parent.defectRate ?? 0.0) * 100)}%</span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-4 flex justify-content-between">
                    <span className="text-600">最大在庫</span><span>{formatMaybe(parent.maxInventory)}</span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-4 flex justify-content-between">
                    <span className="text-600">最小ロット</span><span>{formatMaybe(parent.minLot)}</span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-4 flex justify-content-between">
                    <span className="text-600">入数／箱</span><span>{formatMaybe(parent.qtyPerBox)}</span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-4 flex justify-content-between">
                    <span className="text-600">入数／PL</span><span>{formatMaybe(parent.qtyPerPL)}</span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-4 flex justify-content-between">
                    <span className="text-600">総注文数</span><span>{formatMaybe(parent.totalOrders)}</span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-4 flex justify-content-between">
                    <span className="text-600">総成形数</span><span>{formatMaybe(parent.totalMolding)}</span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-4 flex justify-content-between">
                    <span className="text-600">箱数</span><span>{formatMaybe(parent.totalBoxCount)}</span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-4 flex justify-content-between">
                    <span className="text-600">PL数</span><span>{formatMaybe(parent.totalPlCount)}</span>
                  </div>
                  <div className="col-12 sm:col-6 md:col-4 flex justify-content-between">
                    <span className="text-600">材料使用量(kg)</span><span>{formatFloat(parent.totalMaterialKg)}</span>
                  </div>
                </div>
                {variants.length > 0 && (
                  <div className="flex flex-column gap-1">
                    <div className="text-sm text-700">成形機バリエーション</div>
                    <div className="grid">
                      {variants.map((v) => (
                        <div key={v.key} className="col-12 sm:col-6 lg:col-6">
                          <div className="border-1 border-200 border-round p-2 text-sm flex flex-column gap-1 h-full">
                            <div className="flex justify-content-between">
                              <span className="text-600">成形機</span>
                              <span>{formatMaybe(v.machine)}</span>
                            </div>
                            <div className="flex justify-content-between">
                              <span className="text-600">成形一覧シート行</span>
                              <span>{formatMaybe(v.sheetIdx)}</span>
                            </div>
                            <div className="flex justify-content-between">
                              <span className="text-600">サイクルタイム(s)</span>
                              <span>{formatFloat(v.cycleTimeSec)}</span>
                            </div>
                            <div className="flex justify-content-between">
                              <span className="text-600">組立工程</span>
                              <span>{formatBoolean(v.requiresAssembly)}</span>
                            </div>
                            <div className="flex justify-content-between">
                              <span className="text-600">二台検査</span>
                              <span>{formatBoolean(v.dualInspection)}</span>
                            </div>
                            <div className="text-600">成形方法：</div>
                            <div className="text-700">{formatMaybe(v.note)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
      </div>
    </SeparablePanel>
  )
}

export default ItemMasterDetail
